import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_CATALOG_API ?? '/api/v1';

export type CatalogStatus = 'active' | 'archived' | 'draft';
export type CategoryStatus = 'active' | 'archived';
export type CollectionType = 'static' | 'smart';
export type ClassificationSource = 'manual' | 'ai' | 'rule' | 'marketplace';

export type CategorySEO = {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
};

export type Catalog = {
  id: string;
  tenant_id: string;
  organization_id: string;
  code: string;
  name: string;
  description?: string;
  status: CatalogStatus;
  version: number;
  metadata?: Record<string, string>;
  created_at: string;
  updated_at: string;
};

export type Category = {
  id: string;
  catalog_id: string;
  tenant_id: string;
  organization_id: string;
  parent_id?: string;
  name: string;
  slug: string;
  code?: string;
  path: string;
  depth: number;
  status: CategoryStatus;
  description?: string;
  hsn_code?: string;
  gst_code?: string;
  seo?: CategorySEO;
  metadata?: Record<string, string>;
  tags?: string[];
  version: number;
  child_count?: number;
  created_at: string;
  updated_at: string;
};

export type CategoryNode = {
  category: Category;
  children?: CategoryNode[];
};

export type CollectionRule = {
  field: string;
  operator: string;
  value: string;
};

export type Collection = {
  id: string;
  tenant_id: string;
  organization_id: string;
  catalog_id?: string;
  name: string;
  code?: string;
  description?: string;
  type: CollectionType;
  product_ids?: string[];
  rules?: CollectionRule[];
  metadata?: Record<string, string>;
  created_at: string;
  updated_at: string;
};

export type ClassificationResult = {
  id: string;
  product_id: string;
  category_id: string;
  catalog_id: string;
  source: ClassificationSource;
  score: number;
  confidence: number;
  marketplace?: string;
  external_id?: string;
  overridden: boolean;
  created_at: string;
};

export type ClassificationSuggestion = {
  category_id: string;
  category_name: string;
  path: string;
  score: number;
  confidence: number;
  source: string;
};

export type MarketplaceCategory = {
  id: string;
  category_id: string;
  marketplace: string;
  external_id: string;
  external_path?: string;
  external_name?: string;
  metadata?: Record<string, string>;
  created_at: string;
  updated_at: string;
};

export type MarketplaceMapResult = {
  product_id: string;
  category_id: string;
  marketplace: string;
  external_id: string;
  external_path?: string;
  mappings?: MarketplaceCategory[];
};

export type CatalogDashboard = {
  stats?: Record<string, unknown>;
  widgets?: Array<{ id: string; title: string; type: string; order?: number }>;
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function catalogFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) throw new Error(`Catalog ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const catalogApi = {
  listCatalogs: () => catalogFetch<Catalog[]>('/catalogs'),
  getCatalog: (id: string) => catalogFetch<Catalog>(`/catalogs/${id}`),
  createCatalog: (input: { code: string; name: string; description?: string; status?: CatalogStatus; metadata?: Record<string, string> }) =>
    catalogFetch<Catalog>('/catalogs', { method: 'POST', body: JSON.stringify(input) }),
  updateCatalog: (id: string, input: Partial<Pick<Catalog, 'name' | 'description' | 'status' | 'metadata'>>) =>
    catalogFetch<Catalog>(`/catalogs/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  deleteCatalog: (id: string) => catalogFetch<void>(`/catalogs/${id}`, { method: 'DELETE' }),
  dashboard: () => catalogFetch<CatalogDashboard>('/catalogs/dashboard'),

  listCategories: (params?: { catalog_id?: string; parent_id?: string; q?: string }) => {
    const qs = new URLSearchParams();
    if (params?.catalog_id) qs.set('catalog_id', params.catalog_id);
    if (params?.parent_id) qs.set('parent_id', params.parent_id);
    if (params?.q) qs.set('q', params.q);
    const query = qs.toString();
    return catalogFetch<Category[]>(`/categories${query ? `?${query}` : ''}`);
  },
  getCategoryTree: (catalogId: string) =>
    catalogFetch<CategoryNode[]>(`/categories/tree?catalog_id=${encodeURIComponent(catalogId)}`),
  getCategory: (id: string) => catalogFetch<Category>(`/categories/${id}`),
  createCategory: (input: {
    catalog_id: string;
    parent_id?: string;
    name: string;
    slug?: string;
    code?: string;
    description?: string;
    hsn_code?: string;
    gst_code?: string;
    seo?: CategorySEO;
    metadata?: Record<string, string>;
    tags?: string[];
  }) => catalogFetch<Category>('/categories', { method: 'POST', body: JSON.stringify(input) }),
  updateCategory: (id: string, input: Partial<Omit<Category, 'id' | 'tenant_id' | 'organization_id' | 'created_at' | 'updated_at'>>) =>
    catalogFetch<Category>(`/categories/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  deleteCategory: (id: string) => catalogFetch<void>(`/categories/${id}`, { method: 'DELETE' }),
  moveCategory: (id: string, newParentId: string) =>
    catalogFetch<Category>(`/categories/${id}/move`, { method: 'POST', body: JSON.stringify({ new_parent_id: newParentId }) }),
  mergeCategory: (id: string, sourceId: string, targetId: string) =>
    catalogFetch<Category>(`/categories/${id}/merge`, { method: 'POST', body: JSON.stringify({ source_id: sourceId, target_id: targetId }) }),
  splitCategory: (id: string, input: { name: string; child_ids?: string[]; product_ids?: string[] }) =>
    catalogFetch<Category>(`/categories/${id}/split`, { method: 'POST', body: JSON.stringify(input) }),
  archiveCategory: (id: string) => catalogFetch<Category>(`/categories/${id}/archive`, { method: 'POST' }),
  restoreCategory: (id: string) => catalogFetch<Category>(`/categories/${id}/restore`, { method: 'POST' }),

  classify: (input: {
    product_id: string;
    category_id: string;
    catalog_id: string;
    source?: ClassificationSource;
    score?: number;
    confidence?: number;
    overridden?: boolean;
  }) => catalogFetch<ClassificationResult>('/classification/classify', { method: 'POST', body: JSON.stringify(input) }),
  suggest: (input: {
    product_id: string;
    product_name: string;
    brand?: string;
    description?: string;
    catalog_id: string;
  }) => catalogFetch<ClassificationSuggestion[]>('/classification/suggest', { method: 'POST', body: JSON.stringify(input) }),
  marketplaceMap: (input: { product_id: string; category_id: string; marketplace: string }) =>
    catalogFetch<MarketplaceMapResult>('/classification/marketplace-map', { method: 'POST', body: JSON.stringify(input) }),

  listCollections: (params?: { catalog_id?: string; type?: CollectionType }) => {
    const qs = new URLSearchParams();
    if (params?.catalog_id) qs.set('catalog_id', params.catalog_id);
    if (params?.type) qs.set('type', params.type);
    const query = qs.toString();
    return catalogFetch<Collection[]>(`/collections${query ? `?${query}` : ''}`);
  },
  getCollection: (id: string) => catalogFetch<Collection>(`/collections/${id}`),
  createCollection: (input: {
    catalog_id?: string;
    name: string;
    code?: string;
    description?: string;
    type?: CollectionType;
    product_ids?: string[];
    rules?: CollectionRule[];
    metadata?: Record<string, string>;
  }) => catalogFetch<Collection>('/collections', { method: 'POST', body: JSON.stringify(input) }),
  updateCollection: (id: string, input: Partial<Omit<Collection, 'id' | 'tenant_id' | 'organization_id' | 'created_at' | 'updated_at'>>) =>
    catalogFetch<Collection>(`/collections/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  deleteCollection: (id: string) => catalogFetch<void>(`/collections/${id}`, { method: 'DELETE' }),
};
